import {
  Children,
  cloneElement,
  ComponentPropsWithoutRef,
  CSSProperties,
  isValidElement,
  useEffect,
  useMemo,
  useState,
} from 'react';

import { ADDITIONAL_THEME_COLORS } from '@kezios/design-system';
import { DefaultMantineColor, useMantineTheme } from '@mantine/core';

export type Digit = '0' | '1' | '2' | '3' | '4' | '5' | '6' | '7' | '8' | '9';
export type ThemeColorName =
  | keyof typeof ADDITIONAL_THEME_COLORS
  | DefaultMantineColor;

export type ThemeColor = `${ThemeColorName}.${Digit}`;

export type IconComponentProps = Omit<
  ComponentPropsWithoutRef<'svg'>,
  'xmlns'
> & {
  size?: CSSProperties['height'];
  color?: CSSProperties['color'] | ThemeColor;
  gradient?: React.ReactElement;
};

export const MANTINE_COLORS_REGEX = /([a-zA-Z]+)\.{1}(\d)/gm;

export const IconComponent = ({
  size,
  color,
  gradient,
  children,
  className,
  ...props
}: IconComponentProps) => {
  const theme = useMantineTheme();

  const [actualColor, setActualColor] = useState<CSSProperties['color']>(
    theme.colors[theme.primaryColor][theme.primaryShade as number]
  );

  const gradientId = useMemo(() => {
    if (!gradient) return undefined;

    return gradient.props.id;
  }, [gradient]);

  useEffect(() => {
    if (!color) return;
    const regexResult = MANTINE_COLORS_REGEX.exec(color);
    // We are resetting the regex index to 0 because we want to be able to use the regex multiple times.
    MANTINE_COLORS_REGEX.lastIndex = 0;
    if (regexResult) {
      const [, colorName, digit] = regexResult;
      if (theme.colors[colorName]) {
        setActualColor(theme.colors[colorName][parseInt(digit)]);
        return;
      }
    }

    if (theme.colors[color]) {
      setActualColor(theme.colors[color][theme.primaryShade as number]);
      return;
    }

    setActualColor(color);
  }, [color]);

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      className={className}
      height={`${(size as number) / 16}rem`}
      width={`${(size as number) / 16}rem`}
      {...props}
      color={actualColor}
    >
      {children &&
        Children.toArray(children).map((child, index) => {
          if (
            typeof child === 'string' ||
            typeof child === 'number' ||
            Array.isArray(child) ||
            isValidElement(child) === false
          )
            return child;

          if (!gradientId) return child;

          if ((child as any).props.fill === 'currentColor') {
            child = cloneElement(child as any, {
              fill: `url(#${gradientId})`,
            });
          }

          if ((child as any).props.stroke === 'currentColor') {
            child = cloneElement(child as any, {
              stroke: `url(#${gradientId})`,
            });
          }

          return child;
        })}
      {gradient && gradient}
    </svg>
  );
};
